import React, { useMemo, useRef, useEffect, useCallback, useState, useId } from 'react'
import PropTypes from 'prop-types'
import Select from 'components/ui/Select'
import { HiCheck } from 'react-icons/hi'
import classNames from 'classnames'
import { components as selectComponents } from 'react-select'

const DEFAULT_COLOR = '#6366F1'

const TagDot = ({ color }) => (
    <span
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color || DEFAULT_COLOR }}
    />
)

const OptionContent = ({ label, color }) => (
    <div className="flex items-center gap-2">
        <TagDot color={color} />
        <span>{label}</span>
    </div>
)

const CustomPlaceholder = (props) => {
    return (
        <selectComponents.Placeholder {...props}>
            {props.children}
        </selectComponents.Placeholder>
    )
}

const StoreTagFilter = ({
    className,
    tags,
    value,
    onChange,
    onMenuClose,
    isLoading,
    placeholder,
    size,
    disabled,
    menuPlacement,
    returnObjects,
    hideSelectedValues,
    disableClearButton,
    searchable,
    instanceId: instanceIdProp,
    inputId: inputIdProp,
    onBlur: onBlurProp,
    onFocus: onFocusProp,
    maxVisibleTags,
    allowTagRemoval,
    menuFooter,
    menuMaxHeight,
    onMenuApply,
    onMenuCancel,
    autoApplyOnMenuClose = true,
    ...rest
}) => {
    const {
        onMenuOpen: onMenuOpenProp,
        style: styleOverrides,
        components: componentsOverride,
        ...selectProps
    } = rest

    const selectRef = useRef(null)
    const containerRef = useRef(null)
    const previousValueLengthRef = useRef(Array.isArray(value) ? value.length : 0)
    const isMenuOpenRef = useRef(false)
    const manualCloseRef = useRef(false)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [hasFocus, setHasFocus] = useState(false)
    const [containerWidth, setContainerWidth] = useState(0)
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === 'undefined') {
            return false
        }
        return window.innerWidth < 768
    })

    const generatedId = useId()
    const selectInstanceId = useMemo(
        () => instanceIdProp ?? `store-tag-filter-${generatedId}`,
        [instanceIdProp, generatedId]
    )

    const selectInputId = useMemo(
        () => inputIdProp ?? `${selectInstanceId}-input`,
        [inputIdProp, selectInstanceId]
    )

    const resolvedAllowTagRemoval = useMemo(() => {
        if (typeof allowTagRemoval === 'boolean') {
            return allowTagRemoval
        }
        return !disableClearButton
    }, [allowTagRemoval, disableClearButton])

    const normalizedMaxVisibleTags = useMemo(() => {
        const shouldLimitVisible = hideSelectedValues || disableClearButton
        if (!shouldLimitVisible) {
            return Infinity
        }
        if (typeof maxVisibleTags !== 'number') {
            return 2
        }
        if (!Number.isFinite(maxVisibleTags) || maxVisibleTags <= 0) {
            return Infinity
        }
        return maxVisibleTags
    }, [maxVisibleTags, hideSelectedValues, disableClearButton])

    const ValueContainer = ({ children, ...props }) => {
        const { hasValue, selectProps } = props
        const inputValue = selectProps?.inputValue || ''

        if (hideSelectedValues && hasValue) {
            const childrenArray = React.Children.toArray(children)
            const filteredChildren = childrenArray.filter(child => child?.type !== selectComponents.MultiValue)

            // Only show placeholder if there's no input value
            if (!inputValue) {
                const hasPlaceholder = filteredChildren.some(
                    child => child?.type === selectComponents.Placeholder || child?.type === CustomPlaceholder
                )

                if (!hasPlaceholder) {
                    filteredChildren.unshift(
                        <selectComponents.Placeholder key="placeholder" {...props}>
                            {placeholder}
                        </selectComponents.Placeholder>
                    )
                }
            }

            return (
                <selectComponents.ValueContainer {...props}>
                    {filteredChildren}
                </selectComponents.ValueContainer>
            )
        }

        return (
            <selectComponents.ValueContainer {...props}>
                {children}
            </selectComponents.ValueContainer>
        )
    }

    const options = useMemo(() => {
        if (!Array.isArray(tags)) {
            return []
        }
        const toNumber = (val) => {
            const n = Number(val)
            return Number.isFinite(n) ? n : Infinity
        }
        const sorted = [...tags].sort((a, b) => {
            const pa = toNumber(a?.priority)
            const pb = toNumber(b?.priority)
            if (pa !== pb) return pa - pb
            // Tie-breaker by name for stable order
            return String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
        })
        return sorted.map(tag => ({
            value: tag.id,
            label: tag.name,
            color: tag.color,
            raw: tag,
        }))
    }, [tags])

    const selectedOptions = useMemo(() => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return []
        }
        const values = Array.isArray(value) ? value : [value]
        if (typeof values[0] === 'object') {
            return values
        }
        const optionById = new Map(options.map(option => [option.value, option]))
        return values.map(item => optionById.get(item)).filter(Boolean)
    }, [value, options])

    const mapToReturnShape = useCallback((optionsArray) => {
        if (!Array.isArray(optionsArray)) {
            return []
        }
        const filtered = optionsArray.filter(Boolean)
        return returnObjects ? filtered : filtered.map(option => option.value)
    }, [returnObjects])

    const notifyMenuClose = useCallback((finalValue) => {
        if (!onMenuClose) {
            return
        }
        if (finalValue !== undefined) {
            onMenuClose(finalValue)
            return
        }
        const derivedValue = mapToReturnShape(selectedOptions)
        onMenuClose(derivedValue)
    }, [mapToReturnShape, onMenuClose, selectedOptions])

    const closeMenu = useCallback((finalValue, options = {}) => {
        const { cause, notify = true } = options
        if (!isMenuOpenRef.current) {
            return
        }

        manualCloseRef.current = true
        isMenuOpenRef.current = false
        setIsMenuOpen(false)

        if (cause === 'external') {
            const selectInstance = selectRef.current
            const internalSelect = selectInstance?.select ?? selectInstance

            internalSelect?.blurInput?.()
            internalSelect?.blur?.()
            selectInstance?.blur?.()
            setHasFocus(false)
        }

        if (notify) {
            notifyMenuClose(finalValue)
        }
    }, [notifyMenuClose])

    const menuScrollRef = useRef(0)

    const handleChange = (selected, actionMeta) => {
        // Save scroll position before react-select re-renders the menu
        const menuList = selectRef.current?.menuListRef || selectRef.current?.select?.menuListRef
        if (menuList) {
            menuScrollRef.current = menuList.scrollTop
            // Restore after re-render
            requestAnimationFrame(() => {
                const ml = selectRef.current?.menuListRef || selectRef.current?.select?.menuListRef
                if (ml && menuScrollRef.current > 0) {
                    ml.scrollTop = menuScrollRef.current
                }
            })
        }
        const sanitized = Array.isArray(selected) ? selected : (selected ? [selected] : [])
        const newLength = sanitized.length
        const previousLength = previousValueLengthRef.current

        const finalValue = mapToReturnShape(sanitized)
        onChange?.(finalValue)

        // Solo auto-aplicar si está habilitado y se están removiendo tags
        if (autoApplyOnMenuClose && newLength < previousLength) {
            setTimeout(() => {
                notifyMenuClose(finalValue)
            }, 0)
        } else if (!autoApplyOnMenuClose && previousLength > 0 && newLength === 0) {
            // Si se limpiaron todas las etiquetas mediante la "x" o removiendo la última
            if (onMenuApply) {
                onMenuApply([], { source: 'auto-clear', force: true })
            } else {
                notifyMenuClose([])
            }
        } else if (!autoApplyOnMenuClose && !isMenuOpenRef.current) {
            const action = actionMeta?.action
            const removedViaChip = action === 'remove-value' || action === 'pop-value' || action === 'clear'
            if (removedViaChip) {
                if (onMenuApply) {
                    onMenuApply(finalValue, { source: 'chip-remove', force: true })
                } else {
                    notifyMenuClose(finalValue)
                }
            }
        }

        previousValueLengthRef.current = newLength
    }

    const handleMenuOpen = useCallback((...args) => {
        isMenuOpenRef.current = true
        setIsMenuOpen(true)
        onMenuOpenProp?.(...args)
    }, [onMenuOpenProp])

    const handleMenuClose = useCallback(() => {
        if (manualCloseRef.current) {
            manualCloseRef.current = false
            return
        }

        // Keep menu open after selection (multi-select behavior)
        // Don't call openMenu('first') as it resets scroll position
        isMenuOpenRef.current = true
        setIsMenuOpen(true)

        const instance = selectRef.current
        const internalSelect = instance?.select ?? instance

        if (internalSelect) {
            internalSelect.focusInput?.()
            internalSelect.focus?.()
        } else {
            instance?.focus?.()
        }
    }, [])

    useEffect(() => {
        const currentLength = Array.isArray(value) ? value.length : 0
        previousValueLengthRef.current = currentLength
    }, [value])

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const handleResize = () => {
            setIsMobileViewport(window.innerWidth < 768)
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }

        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (!containerRef.current) {
            return
        }

        const node = containerRef.current

        const syncWidth = () => {
            setContainerWidth(node.offsetWidth)
        }

        syncWidth()

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver((entries) => {
                if (entries[0]) {
                    setContainerWidth(entries[0].contentRect.width)
                }
            })
            observer.observe(node)
            return () => observer.disconnect()
        }

        return () => {}
    }, [])

    useEffect(() => {
        if (!isMenuOpen) {
            return
        }

        const handlePointerDown = (event) => {
            const target = event.target
            const path = typeof event.composedPath === 'function' ? event.composedPath() : undefined

            const isInContainer = containerRef.current
                ? path?.includes(containerRef.current) ?? containerRef.current.contains(target)
                : false

            if (isInContainer) {
                return
            }

            const menuList = selectRef.current?.menuListRef
            const menuContainer = menuList?.parentNode ??
                document.getElementById(`${selectInstanceId}__listbox`) ??
                document.getElementById(`${selectInstanceId}-listbox`)

            const isInMenu = menuContainer
                ? path?.includes(menuContainer) ?? menuContainer.contains(target)
                : false

            if (isInMenu) {
                return
            }
            closeMenu(undefined, { cause: 'external' })
        }

        document.addEventListener('pointerdown', handlePointerDown, true)

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true)
        }
    }, [closeMenu, isMenuOpen, selectInstanceId])

    const handleBlur = useCallback((event) => {
        setHasFocus(false)
        onBlurProp?.(event)
    }, [onBlurProp])

    const handleFocus = useCallback((event) => {
        setHasFocus(true)
        onFocusProp?.(event)
    }, [onFocusProp])

    const handleInputChange = useCallback((inputValue, actionMeta) => {
        return inputValue
    }, [])

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            manualCloseRef.current = true
        }
        if (event.key === 'Tab') {
            manualCloseRef.current = true
        }
    }, [])

    const optionRenderer = (props) => {
        const { innerProps, label, data, isSelected } = props
        return (
            <div
                {...innerProps}
                className={classNames(
                    'flex items-center justify-between px-2 py-2 cursor-pointer',
                    isSelected
                        ? 'bg-blue-500/20 dark:bg-blue-500/20 text-blue-700 dark:text-blue-100'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                )}
            >
                <OptionContent label={label} color={data.color} />
                {isSelected && (
                    <HiCheck className="text-blue-600 dark:text-blue-200 text-lg" />
                )}
            </div>
        )
    }

    const multiValueLabel = (props) => {
        const { data, children } = props
        // Si children es un elemento React (ej: el "+N" inyectado por MultiValue cuando colapsa),
        // renderizarlo tal cual sin TagDot ni data.label.
        if (children && typeof children !== 'string' && typeof children !== 'number') {
            return <div className="flex items-center pl-1">{children}</div>
        }
        return (
            <div className="flex items-center gap-1 pl-1 min-w-0 overflow-hidden">
                <TagDot color={data.color} />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">{data.label}</span>
            </div>
        )
    }

    const MultiValue = (props) => {
        const { index, getValue } = props
        const values = getValue()

        if (!Number.isFinite(normalizedMaxVisibleTags)) {
            return (
                <selectComponents.MultiValue {...props}>
                    {props.children}
                </selectComponents.MultiValue>
            )
        }

        if (values.length <= normalizedMaxVisibleTags || index < normalizedMaxVisibleTags) {
            return (
                <selectComponents.MultiValue {...props}>
                    {props.children}
                </selectComponents.MultiValue>
            )
        }

        if (index === normalizedMaxVisibleTags) {
            const remaining = values.length - normalizedMaxVisibleTags
            if (remaining <= 0) {
                return null
            }
            // El "+N" se inyecta como children custom; el multiValueLabel lo detecta y lo renderiza
            // sin TagDot, manteniendo el wrapper de selectComponents.MultiValue para layout correcto.
            return (
                <selectComponents.MultiValue {...props}>
                    <span className="px-1 text-xs font-semibold text-gray-700 dark:text-gray-100">+{remaining}</span>
                </selectComponents.MultiValue>
            )
        }

        return null
    }

    const selectedOptionsRef = useRef(selectedOptions)
    useEffect(() => { selectedOptionsRef.current = selectedOptions }, [selectedOptions])

    const CustomMenu = useMemo(() => {
        const Menu = (props) => {
            const { children } = props

            const handleApply = (overrideValue) => {
                const finalValue = overrideValue !== undefined
                    ? overrideValue
                    : mapToReturnShape(selectedOptionsRef.current)

                if (onMenuApply) {
                    onMenuApply(finalValue, { source: 'footer', force: true })
                    closeMenu(finalValue, { cause: 'external', notify: false })
                } else {
                    closeMenu(finalValue, { cause: 'external' })
                }
            }

            const handleCancel = () => {
                onMenuCancel?.()
                closeMenu(undefined, { cause: 'external' })
            }

            if (!menuFooter) {
                return <selectComponents.Menu {...props}>{children}</selectComponents.Menu>
            }

            return (
                <selectComponents.Menu {...props}>
                    {children}
                    <div className="border-t border-gray-200 dark:border-gray-600 p-2 bg-white dark:bg-gray-800">
                        {typeof menuFooter === 'function'
                            ? menuFooter({
                                  apply: handleApply,
                                  cancel: handleCancel,
                                  closeMenu: () => closeMenu(undefined, { cause: 'external', notify: false }),
                                  selectedOptions: selectedOptionsRef.current,
                              })
                            : menuFooter}
                    </div>
                </selectComponents.Menu>
            )
        }
        return Menu
    }, [menuFooter, onMenuApply, closeMenu, onMenuCancel, mapToReturnShape])

    const computedMenuDimensions = useMemo(() => {
        if (isMobileViewport) {
            const base = {
                minWidth: undefined,
                width: undefined,
                maxHeight: '220px',
            }
            const overridden = typeof menuMaxHeight !== 'undefined'
                ? { maxHeight: typeof menuMaxHeight === 'number' ? `${menuMaxHeight}px` : String(menuMaxHeight) }
                : {}
            return { ...base, ...overridden }
        }

        const fallbackWidth = Number.isFinite(containerWidth) && containerWidth > 0 ? containerWidth : 280
        const desiredWidth = Math.max(280, fallbackWidth)
        const roundedWidth = Math.round(desiredWidth)

        const base = {
            minWidth: `${roundedWidth}px`,
            width: `${roundedWidth}px`,
            maxHeight: '320px',
        }
        const overridden = typeof menuMaxHeight !== 'undefined'
            ? {
                maxHeight: typeof menuMaxHeight === 'number' ? `${menuMaxHeight}px` : String(menuMaxHeight),
            }
            : {}
        return { ...base, ...overridden }
    }, [containerWidth, isMobileViewport, menuMaxHeight])

    const selectStyles = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            height: hideSelectedValues ? '44px' : 'auto',
            minHeight: hideSelectedValues ? '44px' : base.minHeight,
            alignItems: hideSelectedValues ? 'center' : 'flex-start',
            boxShadow: state.isFocused && hasFocus ? base.boxShadow : 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : base.opacity,
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: hideSelectedValues ? 'nowrap' : 'wrap',
            alignItems: hideSelectedValues ? base.alignItems : 'flex-start',
            overflow: hideSelectedValues ? base.overflow : 'visible',
            gap: '0.25rem',
            paddingTop: hideSelectedValues ? base.paddingTop : '0.375rem',
            paddingBottom: hideSelectedValues ? base.paddingBottom : '0.375rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            pointerEvents: 'auto',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
            minWidth: 0,
            width: '100%',
            pointerEvents: 'auto',
        }),
        menu: (base) => ({
            ...base,
            minWidth: computedMenuDimensions.minWidth ?? base.minWidth,
            width: computedMenuDimensions.width ?? base.width,
            maxHeight: computedMenuDimensions.maxHeight ?? base.maxHeight,
            overflow: 'hidden',
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: computedMenuDimensions.maxHeight ?? base.maxHeight,
        }),
        multiValue: (base) => ({
            ...base,
            maxWidth: '100%',
            display: hideSelectedValues ? 'none' : base.display,
        }),
        multiValueLabel: (base) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        multiValueRemove: (base) => ({
            ...base,
            display: resolvedAllowTagRemoval ? base.display : 'none',
        }),
    }), [computedMenuDimensions, disabled, hasFocus, hideSelectedValues, resolvedAllowTagRemoval])

    const mergedSelectStyles = useMemo(() => {
        if (!styleOverrides || typeof styleOverrides !== 'object') {
            return selectStyles
        }

        const mergedEntries = { ...selectStyles }

        Object.entries(styleOverrides).forEach(([slot, override]) => {
            const baseSlotFn = selectStyles[slot]

            mergedEntries[slot] = (provided, state) => {
                const baseResult = typeof baseSlotFn === 'function'
                    ? baseSlotFn(provided, state)
                    : (baseSlotFn ?? provided)

                if (typeof override === 'function') {
                    return override(baseResult, state)
                }

                if (override && typeof override === 'object') {
                    return { ...baseResult, ...override }
                }

                return baseResult
            }
        })

        return mergedEntries
    }, [selectStyles, styleOverrides])

    const handleContainerClick = useCallback((event) => {
        if (disabled) {
            return
        }

        if (isMenuOpenRef.current) {
            return
        }

        const target = event.target
        if (
            target.closest('.select__clear-indicator') ||
            target.closest('.select__multi-value__remove')
        ) {
            return
        }

        const instance = selectRef.current
        if (!instance) {
            return
        }

        const internalSelect = instance.select ?? instance

        if (typeof internalSelect?.focus === 'function') {
            internalSelect.focus()
        } else if (typeof instance.focus === 'function') {
            instance.focus()
        }

        internalSelect?.focusInput?.()

        isMenuOpenRef.current = true
        setIsMenuOpen(true)

        if (typeof instance.openMenu === 'function') {
            instance.openMenu('first')
        } else if (internalSelect && typeof internalSelect.openMenu === 'function') {
            internalSelect.openMenu('first')
        }
    }, [disabled])

    // Paso 6: Handler para mejorar interacción móvil con toque
    const handleContainerPointerDown = useCallback((event) => {
        if (disabled || isMenuOpenRef.current) {
            return
        }

        // Verificar si el toque es en botones de acción (clear, remove)
        const target = event.target
        if (
            target.closest('.select__clear-indicator') ||
            target.closest('.select__multi-value__remove')
        ) {
            return
        }

        // Prevenir propagación para asegurar que el Select reciba el foco
        event.stopPropagation()

        const instance = selectRef.current
        if (!instance) {
            return
        }

        // Forzar foco en el select, crítico para móviles
        if (typeof instance.focus === 'function') {
            instance.focus()
        } else if (instance.select && typeof instance.select.focus === 'function') {
            instance.select.focus()
        }

        const internalSelect = instance.select ?? instance
        internalSelect?.focusInput?.()
    }, [disabled])

    return (
        <div
            ref={containerRef}
            className={disabled ? 'opacity-60 pointer-events-none cursor-not-allowed' : ''}
            onClick={handleContainerClick}
            onPointerDown={handleContainerPointerDown}
        >
            <Select
                ref={selectRef}
                className={className}
                isMulti
                isClearable={!disableClearButton}
                isSearchable={searchable}
                hideSelectedOptions={false}
                value={selectedOptions}
                options={options}
                placeholder={placeholder}
                isLoading={isLoading}
                size={size}
                isDisabled={disabled}
                menuPlacement={menuPlacement}
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                openMenuOnClick={true}
            tabIndex={disabled ? -1 : 0}
            onChange={handleChange}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onBlur={handleBlur}
            onFocus={handleFocus}
            menuIsOpen={isMenuOpen}
            onInputChange={handleInputChange}
            onKeyDown={handleKeyDown}
                noOptionsMessage={() => searchable ? 'No se encontraron resultados' : 'No hay opciones disponibles'}
                loadingMessage={() => 'Cargando...'}
                components={{
                    Option: optionRenderer,
                    MultiValueLabel: multiValueLabel,
                    MultiValue,
                    ValueContainer,
                    Menu: CustomMenu,
                    Placeholder: CustomPlaceholder,
                    ...(componentsOverride || {}),
                }}
                style={mergedSelectStyles}
                instanceId={selectInstanceId}
                inputId={selectInputId}
                {...selectProps}
            />
        </div>
    )
}

StoreTagFilter.propTypes = {
    className: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
    })),
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    onChange: PropTypes.func,
    onMenuClose: PropTypes.func,
    isLoading: PropTypes.bool,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    size: PropTypes.string,
    disabled: PropTypes.bool,
    menuPlacement: PropTypes.oneOf(['auto', 'top', 'bottom']),
    returnObjects: PropTypes.bool,
    hideSelectedValues: PropTypes.bool,
    disableClearButton: PropTypes.bool,
    searchable: PropTypes.bool,
    backspaceRemovesValue: PropTypes.bool,
    instanceId: PropTypes.string,
    inputId: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    maxVisibleTags: PropTypes.number,
    allowTagRemoval: PropTypes.bool,
    menuFooter: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    onMenuApply: PropTypes.func,
    onMenuCancel: PropTypes.func,
    autoApplyOnMenuClose: PropTypes.bool,
    menuMaxHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    style: PropTypes.object,
}

StoreTagFilter.defaultProps = {
    tags: [],
    value: [],
    placeholder: 'Filtrar por etiquetas',
    menuPlacement: 'auto',
    returnObjects: false,
    hideSelectedValues: false,
    disableClearButton: false,
    searchable: true,
    backspaceRemovesValue: true,
    maxVisibleTags: 2,
    menuMaxHeight: undefined,
}

export default StoreTagFilter
